/**
 * useEffectivePermissions — React hook over the Phase 2 Access-Control Engine.
 *
 * Fetches /api/me/effective-permissions once per session (cached in module scope) and
 * exposes ergonomic helpers for UI gating:
 *
 *   const perms = useEffectivePermissions();
 *   if (perms.can('employees', 'edit')) {...}
 *   if (perms.canSeeTier('payroll_restricted')) {...}
 *   perms.scope.type  // "all" | "team" | "region" | ...
 *   perms.role        // "admin" | "supervisor" | ...
 *
 * The hook is intentionally simple — no React Query / no complex revalidation. It
 * piggybacks on the existing AuthContext: it refetches when the user object changes
 * (login / logout / role change).
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

// Module-scope cache keyed by user id so multiple hook instances share one network call.
let _cache = null;          // { user_id, data, promise }

const EMPTY_PERMS = {
  loaded: false,
  role: null,
  add_on_roles: [],
  capabilities: {},          // { module: ["view","edit",...] }
  accessible_tiers: ['operational'],
  scope: { type: 'all', value: null },
  hr_grants: [],
  can: () => false,
  canModule: () => false,
  canSeeTier: () => false,
  hasGrant: () => false,
  refresh: async () => {},
};

function buildHelpers(data) {
  const caps = data.capabilities || {};
  const tiers = new Set(data.accessible_tiers || ['operational']);
  const grants = new Set(data.hr_grants || []);
  const can = (module, capability) => {
    const list = caps[module] || [];
    return list.includes(capability);
  };
  const canModule = (module) => Boolean(caps[module] && caps[module].length);
  const canSeeTier = (tier) => tiers.has(tier);
  const hasGrant = (g) => grants.has(g);
  return { can, canModule, canSeeTier, hasGrant };
}

export function useEffectivePermissions() {
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY_PERMS);

  const load = useCallback(async () => {
    if (!user) { setState(EMPTY_PERMS); return; }
    // Use cached value if same user
    if (_cache && _cache.user_id === user.id && _cache.data) {
      setState({ ...EMPTY_PERMS, ...(_cache.data), loaded: true, ...buildHelpers(_cache.data), refresh: load });
      return;
    }
    // De-dupe in-flight requests
    if (_cache && _cache.user_id === user.id && _cache.promise) {
      const data = await _cache.promise;
      setState({ ...EMPTY_PERMS, ...data, loaded: true, ...buildHelpers(data), refresh: load });
      return;
    }
    const promise = api.get('/me/effective-permissions').then(r => r.data).catch(() => null);
    _cache = { user_id: user.id, data: null, promise };
    const data = await promise;
    if (!data) { setState(EMPTY_PERMS); _cache = null; return; }
    _cache = { user_id: user.id, data, promise: null };
    setState({ ...EMPTY_PERMS, ...data, loaded: true, ...buildHelpers(data), refresh: load });
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Bust the cache on logout / user switch
  useEffect(() => {
    if (!user && _cache) _cache = null;
  }, [user]);

  return state;
}

// Allow imperative cache busting from auth flows (e.g., after PUT /users/{id}/access on self).
export function clearEffectivePermissionsCache() {
  _cache = null;
}
